import { api } from '../client';
import type {
  AdminTruckType,
  CreateTruckTypeRequest,
  TruckCatalogCategory,
  TruckCatalogResponse,
  TruckCatalogTruck,
  UpdateTruckTypeRequest,
} from '../types';

/**
 * Truck-type catalog (#249 Contract sections 2–3). The public list feeds every picker; the
 * admin list and writes feed Settings → Truck types. There is no delete: hide with `active:false`.
 */

const str = (v: unknown): string | null => (typeof v === 'string' ? v : null);
const rec = (v: unknown): Record<string, unknown> | null =>
  v && typeof v === 'object' ? (v as Record<string, unknown>) : null;

function parseTruck(raw: unknown): TruckCatalogTruck | null {
  const t = rec(raw);
  const id = str(t?.id);
  const name = str(t?.name);
  if (!t || !id || !name) return null;
  return { id, name, capacityTons: str(t.capacityTons), lengthFt: str(t.lengthFt), idealFor: str(t.idealFor) };
}

/** Tolerates an older API: missing `updatedAt`, `description`, `trucks` or null fields never throw. */
export function parseTruckCatalog(raw: unknown): TruckCatalogResponse {
  const body = rec(raw);
  const categories: TruckCatalogCategory[] = [];
  for (const c of Array.isArray(body?.categories) ? body.categories : []) {
    const cat = rec(c);
    const categoryId = str(cat?.categoryId);
    if (!cat || !categoryId) continue;
    categories.push({
      categoryId,
      categoryName: str(cat.categoryName) ?? categoryId,
      description: str(cat.description) ?? '',
      trucks: (Array.isArray(cat.trucks) ? cat.trucks : [])
        .map(parseTruck)
        .filter((t): t is TruckCatalogTruck => t !== null),
    });
  }
  return { categories, updatedAt: str(body?.updatedAt) };
}

export function parseAdminTruckType(raw: unknown): AdminTruckType {
  const t = rec(raw) ?? {};
  return {
    id: str(t.id) ?? '',
    categoryId: str(t.categoryId) ?? '',
    categoryName: str(t.categoryName) ?? str(t.categoryId) ?? '',
    name: str(t.name) ?? '',
    capacityTons: str(t.capacityTons),
    lengthFt: str(t.lengthFt),
    idealFor: str(t.idealFor),
    sortOrder: typeof t.sortOrder === 'number' ? t.sortOrder : 0,
    active: t.active !== false,
    version: typeof t.version === 'number' ? t.version : 0,
    updatedAt: str(t.updatedAt) ?? '',
  };
}

export async function getTruckCatalog(signal?: AbortSignal): Promise<TruckCatalogResponse> {
  const { data } = await api.get<unknown>('/truck-types', { signal });
  return parseTruckCatalog(data);
}

export async function listAdminTruckTypes(signal?: AbortSignal): Promise<AdminTruckType[]> {
  const { data } = await api.get<unknown>('/admin/truck-types', { signal });
  const trucks = rec(data)?.trucks;
  return (Array.isArray(trucks) ? trucks : []).map(parseAdminTruckType);
}

export async function createTruckType(body: CreateTruckTypeRequest): Promise<AdminTruckType> {
  const { data } = await api.post<unknown>('/admin/truck-types', body);
  return parseAdminTruckType(data);
}

export async function updateTruckType(id: string, body: UpdateTruckTypeRequest): Promise<AdminTruckType> {
  const { data } = await api.patch<unknown>(`/admin/truck-types/${encodeURIComponent(id)}`, body);
  return parseAdminTruckType(data);
}
